/**
 * InlineEditableText — click-to-edit text widget shared by:
 *   - the short Impact summary in the Workflow Selector table cell (single)
 *   - the long Impact paragraph in the expanded row detail (multi)
 *
 * Behavior is driven by the `variant` prop:
 *
 *   • idle → click (or pencil) → editing
 *   • editing:
 *       - single: <input>, no word cap. Users can type any length; callers
 *         that render the value in a constrained column should truncate
 *         with ellipsis.
 *       - multi: auto-growing <textarea> (3–8 rows), soft-guidance text
 *   • Save → calls `onSave(next)`; on resolve return to idle, on reject
 *     revert to prior value and surface a toast (MUI Snackbar).
 *   • Cancel button or Esc → revert without saving (Esc always cancels).
 *   • Save-on-blur fires when focus leaves the control to anywhere outside
 *     the edit affordance.
 *
 * Toast primitive: this component embeds a local MUI <Snackbar> (the same
 * primitive `App.tsx` uses) for error display, since the project does not
 * have a global toast/snackbar context (see notes attached to this
 * implementation).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { toUserFacingError } from '../utils/userFacingError';

export type InlineEditableTextProps = {
  value: string;
  onSave: (next: string) => Promise<void>;
  variant: 'single' | 'multi';
  emptyPlaceholder?: string;
  /**
   * Accessible label applied to the edit input/textarea when editing. The
   * idle-state Box is no longer interactive (only the pencil IconButton
   * triggers edit mode), so this label does NOT attach to the idle Box.
   */
  ariaLabel?: string;
  /**
   * Accessible label + tooltip for the pencil edit button. Defaults to
   * "Edit Impact Summary" for backward compatibility with the original
   * (impact-only) callers. Reusing this component for other fields (e.g.
   * workflow name/description) should pass a distinct label so multiple
   * editors on the same screen remain individually addressable.
   */
  editButtonLabel?: string;
};

type State = 'idle' | 'editing' | 'saving' | 'error';

const MULTI_MIN_ROWS = 3;
const MULTI_MAX_ROWS = 8;

export const InlineEditableText: React.FC<InlineEditableTextProps> = ({
  value,
  onSave,
  variant,
  emptyPlaceholder,
  ariaLabel,
  editButtonLabel = 'Edit Impact Summary',
}) => {
  const [state, setState] = useState<State>('idle');
  const [draft, setDraft] = useState<string>(value);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  // Keep draft in sync if `value` prop changes externally while idle.
  useEffect(() => {
    if (state === 'idle') {
      setDraft(value);
    }
  }, [value, state]);

  // When entering edit mode, focus + select-all so the user can type
  // immediately or replace.
  useEffect(() => {
    if (state === 'editing' && inputRef.current) {
      inputRef.current.focus();
      try {
        // For single line input.select(); textarea also supports select().
        (inputRef.current as HTMLInputElement | HTMLTextAreaElement).select?.();
      } catch {
        /* no-op */
      }
    }
  }, [state]);

  const enterEdit = useCallback(() => {
    if (state === 'idle') {
      setDraft(value);
      setState('editing');
    }
  }, [state, value]);

  const cancel = useCallback(() => {
    setDraft(value);
    setState('idle');
  }, [value]);

  const save = useCallback(async () => {
    // Don't bother calling onSave if draft is unchanged.
    if (draft === value) {
      setState('idle');
      return;
    }
    setState('saving');
    try {
      await onSave(draft);
      setState('idle');
    } catch (err: any) {
      // Revert and surface a toast.
      setDraft(value);
      setErrorMessage(
        err instanceof Error ? err.message : 'Failed to save changes'
      );
      setState('error');
      // Drop back to idle so the user can re-enter edit mode if desired.
      // The error snackbar remains visible until dismissed.
      setTimeout(() => setState('idle'), 0);
    }
  }, [draft, value, onSave]);

  // Save on blur: if focus moves outside the container, save.
  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      if (state !== 'editing') return;
      const next = event.relatedTarget as Node | null;
      if (next && containerRef.current?.contains(next)) {
        // Focus moved within the edit affordance (e.g., to Save/Cancel).
        return;
      }
      // Focus left the control — save.
      void save();
    },
    [state, save]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        cancel();
        return;
      }
      // Enter saves in single-line variant; multi-line variant lets Enter
      // insert a newline (use the explicit Save button or blur to commit).
      if (event.key === 'Enter' && variant === 'single') {
        event.preventDefault();
        void save();
      }
    },
    [cancel, save, variant]
  );

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setDraft(event.target.value);
  };

  // ----- IDLE / SAVING render ---------------------------------------------
  if (state !== 'editing') {
    const showSavingHint = state === 'saving';
    const isEmpty = !value || value.trim() === '';
    return (
      <Box
        ref={containerRef}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'text',
          minHeight: 24,
          minWidth: 0,
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        <Typography
          component="span"
          variant="body2"
          color={isEmpty ? 'text.secondary' : 'text.primary'}
          sx={{
            whiteSpace: variant === 'single' ? 'nowrap' : 'pre-wrap',
            overflow: variant === 'single' ? 'hidden' : 'visible',
            textOverflow: variant === 'single' ? 'ellipsis' : 'clip',
            opacity: showSavingHint ? 0.6 : 1,
            flex: '1 1 auto',
            minWidth: 0,
          }}
          title={variant === 'single' && !isEmpty ? value : undefined}
        >
          {isEmpty ? emptyPlaceholder ?? '' : value}
        </Typography>
        <Tooltip title={editButtonLabel} arrow>
          <IconButton
            size="small"
            className="inline-edit-pencil"
            aria-label={editButtonLabel}
            onClick={(e) => {
              e.stopPropagation();
              enterEdit();
            }}
            sx={{
              opacity: 1,
              p: 0.25,
              flexShrink: 0,
            }}
          >
            <EditIcon fontSize="inherit" sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
        <Snackbar
          open={!!errorMessage}
          autoHideDuration={4000}
          onClose={() => setErrorMessage(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity="error"
            variant="filled"
            onClose={() => setErrorMessage(null)}
            sx={{ width: '100%' }}
          >
            {toUserFacingError(errorMessage)}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // ----- EDITING render ----------------------------------------------------
  // Multi variant uses block layout so the textarea fills the parent's
  // available width (e.g., the expanded-row Impact section). Single variant
  // keeps the narrower inline-flex layout used by table cells.
  return (
    <Box
      ref={containerRef}
      onBlur={handleBlur}
      // While editing, swallow clicks so they never reach an ancestor row-open
      // handler (e.g. FlowTable's AccordionSummary onClick). Without this, the
      // Name cell — which intentionally lets idle clicks bubble to open the
      // flow — would also open the flow when the user clicks inside the text
      // field or the Save/Cancel buttons. Harmless for callers whose cell
      // already stops propagation (Impact, Description).
      onClick={(e) => e.stopPropagation()}
      sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}
    >
      {variant === 'single' ? (
        <TextField
          inputRef={inputRef}
          value={draft}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          size="small"
          fullWidth
          inputProps={{ 'aria-label': ariaLabel ?? 'Edit text' }}
          autoFocus
        />
      ) : (
        <TextField
          inputRef={inputRef}
          value={draft}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          size="small"
          fullWidth
          multiline
          minRows={MULTI_MIN_ROWS}
          maxRows={MULTI_MAX_ROWS}
          inputProps={{ 'aria-label': ariaLabel ?? 'Edit text' }}
          autoFocus
        />
      )}

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button size="small" variant="contained" onClick={save}>
          Save
        </Button>
        <Button size="small" onClick={cancel}>
          Cancel
        </Button>
      </Stack>
    </Box>
  );
};

export default InlineEditableText;
